import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import moment from 'moment'
import numeral from 'numeral'
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Sector,
} from 'recharts'
import { useSelector } from 'react-redux'

import styles from './styles.module.scss'

import DRP from '../../../components/Core/DRP/DRP'
import { ClockIcon, ExpenseEntriesIcon, ActiveProjectIcon, CustomersBilledIcon, WalletIcon, DollarIcon, ArrowIcon, PercentIcon } from './icons'
import Progress from '../Progress'
import AppNavbar from '../../Core/AppNavbar'
import ProfitabilityPlaces from './Places'
import ProfitabilityCustomers from './Customers'
import ProfitabilityExpenses from './Expenses'
import ProfitabilityEmployees from './Employees'

import { getPlaceProfitability } from '../../../api'
import { getCurrency } from '../../../helpers/currencies'

const COST_COLORS = ['#6366f1', '#f59e0b']
const REVENUE_COLOR = '#10b981'
const PROFIT_COLOR = '#0ea5e9'

const ActiveSector = (props) => {
  const { outerRadius, fill } = props
  return (
    <g>
      <Sector {...props} outerRadius={outerRadius + 8} />
    </g>
  )
}

const TrandsDataPlaceholder = () => {
  const { t } = useTranslation()

  return (
    <div className={styles.emptyChart}>
      <div className={styles.emptyChart__content}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path d="M3 3v16a2 2 0 0 0 2 2h16M18 17V9M13 17V5M8 17v-3" />
        </svg>

        <p className={styles.emptyChart__title}>
          {t('No activity for this period')}
        </p>
      </div>
    </div>
  )
}

const CompositionPlaceholder = () => {
  const { t } = useTranslation()
  
  return (
    <div className={styles.emptyComposition}>
      <svg style={{width: 170, height: 170}} viewBox="0 0 100 100">
        <circle cx={50} cy={50} r={38} fill="none" stroke="#e0e5eb" className="stroke-border/50" strokeWidth="14" strokeDasharray="4 4" />
        <circle cx={50} cy={50} r={38} fill="none" stroke="#e0e5eb" className="stroke-border/30" strokeWidth="1" />
      </svg>
      <div>{t('No costs yet')}</div>
    </div>
  )
}

const formatNumber = (value, currency) => {
  if (!numeral(value).value()) {
    return '-'
  }
  return `${numeral(value).format('0,0.00')} ${currency}`
}

const Profitability = ({companyId, placeId, selectedPlace}) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)

  const contentRef = useRef()

  const currency = getCurrency(companyInfo?.currency)

  const [activeTab, setActiveTab] = useState('projects')
  const [filterDate, setFilterDate] = useState({
    start: moment().startOf('month'),
    end: moment()
  })
  const [aggregated, setAggregated] = useState({
    loading: false,
    total_cost: {
      total: 0,
      work_times: 0,
      expenses: 0
    },
    revenue: {
      issued: 0,
      paid: 0,
      unpaid: 0
    },
    profit: {
      total: 0,
      count: 0,
    },
    margin: 0,
    hours_count: 0,
    expenses_count: 0,
    places_count: 0,
    customers_count: 0,
    trends_data: [],
    breakdown_data: {
      places_aggregated_data: [],
      customers_aggregated_data: [],
      expenses_aggregated_data: [],
      employees_aggregated_data: [],
    }
  })

  const [pieActiveIndex, setPieActiveIndex] = useState(-1)

  const costSplit = [
    { name: t('Work entries'), value: aggregated.total_cost.work_times },
    { name: t('Expenses'), value: aggregated.total_cost.expenses },
  ]

  const breakdownOptions = [
    ...(!placeId ? [{ key: 'projects', title: t('Projects') }] : []),
    { key: 'customers', title: t('Customers') },
    { key: 'expenses', title: t('Expenses') },
    { key: 'employees', title: t('Employees') },
  ]

  const isContainTrandsData = aggregated.trends_data.some(item => item.total_cost > 0 || item.revenue_issued > 0 || item.profit_total > 0)
  const isContainCostSplitData = costSplit.some(item => item.value > 0)

  useEffect(() => {
    const rect = contentRef.current.getBoundingClientRect()
    const distance = window.innerHeight - rect.top;
    contentRef.current.style.height = `${distance - 35}px`
  }, [])

  useEffect(() => {
    if (placeId && activeTab === 'projects') {
      setActiveTab('customers')
    }
  }, [placeId, activeTab])

  useEffect(() => {
    getProfitability()
  }, [companyId, placeId, filterDate.start.format('YYYY-MM-DD'), filterDate.end.format('YYYY-MM-DD')])

  const getProfitability = async () => {
    setAggregated(prev => ({...prev, loading: true}))
    const res = await getPlaceProfitability(companyId, placeId ? placeId : 'all', {
      start_date: filterDate.start.format('YYYY-MM-DD'),
      end_date: filterDate.end.format('YYYY-MM-DD')
    })
    if (res) {
      setAggregated({...res, loading: false})
      return
    }
    setAggregated(prev => ({...prev, loading: false}))
  }

  const handleChangeDate = ({startDate, endDate}) => {
    setFilterDate({start: moment(startDate), end: moment(endDate)})
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{flex: 1}}>
          <div className={styles.headerTitle}>{placeId ? `${selectedPlace.name} ${t('profitability')}` : t('Profitability')}</div>
          <div className={styles.headerDesc}>{t('Live view of all hours, expenses, invoiced revenue and project margin.')}</div>
        </div>
        <DRP
          right={true}
          additionalRanges={[
            {
              label: 'This year',
              startDate: moment().startOf('year').toDate(),
              endDate: moment().toDate()
            }, {
              label: 'Last year',
              startDate: moment().subtract(1, 'year').startOf('year').toDate(),
              endDate: moment().subtract(1, 'year').endOf('year').toDate()
            }
          ]}
          definedRangesStyle={{right: 40}}
          initRange={{startDate: filterDate.start.toDate(), endDate: filterDate.end.toDate()}}
          onChange={handleChangeDate} />
      </div>
      <div className={styles.content} ref={contentRef}>
        <div className={cn(styles.aggregateContainer, {[styles.empty]: !aggregated.loading && !aggregated.hours_count && !aggregated.expenses_count && !aggregated.places_count && !aggregated.customers_count})}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>{t('Total cost')}</div>
              <WalletIcon color="#0b111f" />
            </div>
            <div className={styles.cardValue}>
              {aggregated.loading
                ? <span className={styles.skeleton} style={{ width: 100, height: 28 }} />
                : formatNumber(aggregated.total_cost.total, currency.symbol)
              }
            </div>
            <div className={styles.cardBody}>
              {
              aggregated.loading
                ? <>
                    <span className={styles.skeleton} style={{ height: 14, marginBottom: 4 }} />
                    <span className={styles.skeleton} style={{ height: 14 }} />
                  </>
                : !aggregated.total_cost.work_times && !aggregated.total_cost.expenses
                  ? <div className={styles.label}>{t('Pick a period to view')}</div>
                  : <>
                      <Progress
                        label={t('Work times')}
                        value={`${numeral(aggregated.total_cost.work_times).format('0,0.00')} ${currency.symbol}`}
                        pct={aggregated.total_cost.total > 0 ? (aggregated.total_cost.work_times / aggregated.total_cost.total) * 100 : 0} />
                      <Progress
                        label={t('Expenses')}
                        value={`${numeral(aggregated.total_cost.expenses).format('0,0.00')} ${currency.symbol} `}
                        pct={aggregated.total_cost.total > 0 ? (aggregated.total_cost.expenses / aggregated.total_cost.total) * 100 : 0} />
                    </>
              }
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>{t('Revenue (issued)')}</div>
              <DollarIcon color="#454bc0" />
            </div>
            <div className={styles.cardValue}>
              {aggregated.loading
                ? <span className={styles.skeleton} style={{ width: 100, height: 28 }} />
                : formatNumber(aggregated.revenue.issued, currency.symbol)
              }
            </div>
            <div className={styles.cardBody}>
              {
                aggregated.loading
                  ? <>
                      <span className={styles.skeleton} style={{ height: 14, marginBottom: 4 }} />
                      <span className={styles.skeleton} style={{ height: 14 }} />
                    </>
                  : !aggregated.revenue.paid && !aggregated.revenue.unpaid
                    ? <div className={styles.label}>{t('Pick a period to view')}</div>
                    : <>
                        <div className={styles.row}>
                          <div className={styles.label}>{t('Paid')}</div>
                          <div className={styles.value}>{numeral(aggregated.revenue.paid).format('0,0.00')} {currency.symbol}</div>
                        </div>
                        <div className={styles.row}>
                          <div className={styles.label}>{t('Outstanding')}</div>
                          <div className={styles.value}>{numeral(aggregated.revenue.unpaid).format('0,0.00')} {currency.symbol} </div>
                        </div>
                      </>
              }
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>{t('Profit')}</div>
              <ArrowIcon color="#009966" />
            </div>
            <div className={styles.cardValue}>
              {aggregated.loading
                ? <span className={styles.skeleton} style={{ width: 90, height: 28 }} />
                : formatNumber(aggregated.profit.total, currency.symbol)
              }
            </div>
            <div className={styles.cardBody}>
              {
                !aggregated.profit.total && !aggregated.loading
                  ? <div className={styles.label}>{t('Pick a period to view')}</div>
                  : null
              }
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>{t('Margin')}</div>
              <PercentIcon color="#e7000b" />
            </div>
            <div className={styles.cardValue}>
              {aggregated.loading
                ? <span className={styles.skeleton} style={{ width: 60, height: 28 }} />
                : aggregated.margin
                    ? `${aggregated.margin}%`
                    : '-'
              }
            </div>
            <div className={styles.cardBody}>
              {
                !aggregated.margin && !aggregated.loading
                  ? <div className={styles.label}>{t('Pick a period to view')}</div>
                  : null
              }
            </div>
          </div>
          {
            !aggregated.loading && !aggregated.hours_count && !aggregated.expenses_count && !aggregated.places_count && !aggregated.customers_count
              ? null
              : <>
                  <div className={cn(styles.card, styles.row)}>
                    <div className={styles.cardIconContainer}>
                      <ClockIcon color="#808F94" />
                    </div>
                    <div className={styles.cardContent}>
                      <div>{ t('Hours logged') }</div>
                      <div className={styles.cardValue}>
                        {aggregated.loading
                          ? <span className={styles.skeleton} style={{ width: 48, height: 18 }} />
                          : aggregated.hours_count}
                      </div>
                    </div>
                  </div>
                  <div className={cn(styles.card, styles.row)}>
                    <div className={styles.cardIconContainer}>
                      <ExpenseEntriesIcon color="#808F94" />
                    </div>
                    <div className={styles.cardContent}>
                      <div>{ t('Expense entries') }</div>
                      <div className={styles.cardValue}>
                        {aggregated.loading
                          ? <span className={styles.skeleton} style={{ width: 48, height: 18 }} />
                          : aggregated.expenses_count}
                      </div>
                    </div>
                  </div>
                  <div className={cn(styles.card, styles.row)}>
                    <div className={styles.cardIconContainer}>
                      <ActiveProjectIcon color="#808F94" />
                    </div>
                    <div className={styles.cardContent}>
                      <div>{ placeId ? t('Project status') : t('Active projects') }</div>
                      <div className={styles.cardValue}>
                        {aggregated.loading
                          ? <span className={styles.skeleton} style={{ width: 48, height: 18 }} />
                          : placeId
                            ? t('Active')
                            : aggregated.places_count
                        }
                      </div>
                    </div>
                  </div>
                  <div className={cn(styles.card, styles.row)}>
                    <div className={styles.cardIconContainer}>
                      <CustomersBilledIcon color="#808F94" />
                    </div>
                    <div className={styles.cardContent}>
                      <div>{ t('Customers billed') }</div>
                      <div className={styles.cardValue}>
                        {aggregated.loading
                          ? <span className={styles.skeleton} style={{ width: 48, height: 18 }} />
                          : aggregated.customers_count}
                      </div>
                    </div>
                  </div>
                </>

          }
        </div>
        <div className={styles.chartsContainer}>
          <div className={styles.card}>
            <div>
              <div className={styles.label}>{t('Cost vs Revenue over time')}</div>
              <div className={styles.desc}>{t('Monthly view, net of VAT')}</div>
            </div>
            <div style={{height: 288}} className={styles.lineChart}>
              {aggregated.loading ? (
                <span className={styles.skeleton} style={{ height: '100%', borderRadius: 8 }} />
              ) : (
                <div style={{width: '100%', height: '100%', position: 'relative'}}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={aggregated.trends_data} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                      {
                        isContainTrandsData
                          ? <CartesianGrid strokeDasharray="3 3" stroke="currentColor" style={{opacity: 0.2}} />
                          : null
                      }
                      {
                        isContainTrandsData
                          ? <XAxis dataKey="date" stroke="currentColor" fontSize={12} />
                          : null
                      }
                      <YAxis stroke="currentColor" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ background: '#fff', color: '#333945', border: '1px solid #CBD1D8', borderRadius: 8, fontSize: 12 }}
                        formatter={(v) => `${numeral(v).format(`0.00`)} ${currency.symbol}`}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {
                        isContainTrandsData
                          ? <>
                              <Line type="monotone" dataKey="total_cost" name={t('Cost')} stroke={COST_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
                              <Line type="monotone" dataKey="revenue_issued" name={t('Revenue')} stroke={REVENUE_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                              <Line type="monotone" dataKey="profit_total" name={t('Profit')} stroke={PROFIT_COLOR} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                            </>
                          : null
                      }
                    </LineChart>
                  </ResponsiveContainer>
                  {
                    !isContainTrandsData
                      ? <TrandsDataPlaceholder />
                      : null
                  }
                </div>
              )}
            </div>
          </div>
          <div className={styles.card}>
            <div>
              <div className={styles.label}>{t('Cost composition')}</div>
              <div className={styles.desc}>{t('Where the money goes')}</div>
            </div>
            <div style={{height: 256}}>
              {aggregated.loading ? (
                <span className={styles.skeleton} style={{ height: '100%', borderRadius: 8 }} />
              ) : (
                isContainCostSplitData
                  ? <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={costSplit}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={2}
                          isAnimationActive
                          animationBegin={0}
                          animationDuration={800}
                          animationEasing="ease-out"
                          activeIndex={pieActiveIndex}
                          activeShape={(p) => <ActiveSector {...p} />}
                          onMouseEnter={(_, idx) => setPieActiveIndex(idx)}
                          onMouseLeave={() => setPieActiveIndex(-1)}>
                          {costSplit.map((_, i) => (
                            <Cell key={i} fill={COST_COLORS[i % COST_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ background: '#fff', color: '#333945', border: '1px solid #CBD1D8', borderRadius: 8, fontSize: 12 }}
                          formatter={(v) => `${numeral(v).format(`0,0.00`)} ${currency.symbol}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  : <CompositionPlaceholder />
              )}
            </div>
            <div>
              {
                costSplit.map((item, i) => {
                  return (
                    <div className={styles.legend} key={i}>
                      <div className={styles.label}>{item.name}</div>
                      <div className={styles.value}>
                        {aggregated.loading
                          ? <span className={styles.skeleton} style={{ width: 60, height: 14, display: 'inline-block' }} />
                          : `${numeral(item.value).format('0,0.00')} ${currency.symbol}`}
                      </div>
                    </div>
                  )
                })
              }
            </div>
          </div>
        </div>
        <div className={styles.breakdownContainer}>
          <div className={styles.breakdownHeader}>
            <div>
              <div className={styles.label}>{t('Profitability breakdown')}</div>
              <div className={styles.desc}>{t('Drill into projects, customers, expenses or employees')}</div>
            </div>
            <AppNavbar
              options={breakdownOptions}
              selected={activeTab}
              className={styles.navbar}
              highlighted={false}
              onSelect={key => setActiveTab(key)}
            />
          </div>
          {
            (tab => {
              switch (tab) {
                case 'projects':
                  return (
                    <ProfitabilityPlaces
                      data={aggregated.breakdown_data.places_aggregated_data} />
                  )
                case 'customers':
                  return (
                    <ProfitabilityCustomers
                      data={aggregated.breakdown_data.customers_aggregated_data} />
                  )
                case 'expenses':
                  return (
                    <ProfitabilityExpenses
                      data={aggregated.breakdown_data.expenses_aggregated_data} />
                  )
                case 'employees':
                  return (
                    <ProfitabilityEmployees
                      data={aggregated.breakdown_data.employees_aggregated_data} />
                  )
                default:
                  return null
              }
            })(activeTab)
          }
        </div>
      </div>
    </div>
  )
}

export default Profitability
